import * as React from 'react';
import * as echarts from 'echarts/core';
import { BarChart, LineChart } from 'echarts/charts';
import {
    GridComponent,
    LegendComponent,
    TooltipComponent,
} from 'echarts/components';
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { SVGRenderer } from 'echarts/renderers';
import type {
    BarSeriesOption,
    GridComponentOption,
    LegendComponentOption,
    LineSeriesOption,
    TooltipComponentOption,
} from 'echarts';
import { Box, Typography } from '@mui/material';

export type SeoWeeklySummaryPoint = {
    data_date: string;
    week_start_date: string;
    week_end_date: string;
    clicks: number;
    impressions: number;
    ctr_percentage: number;
    average_position: number | null;
};

type ECOption = echarts.ComposeOption<
    | LineSeriesOption
    | BarSeriesOption
    | TooltipComponentOption
    | GridComponentOption
    | LegendComponentOption
>;

echarts.use([
    TooltipComponent,
    GridComponent,
    LegendComponent,
    LineChart,
    BarChart,
    LabelLayout,
    UniversalTransition,
    SVGRenderer,
]);

const formatWeekLabel = (row: SeoWeeklySummaryPoint): string => {
    if (row.week_end_date) {
        const date = new Date(`${row.week_end_date}T00:00:00`);
        if (!Number.isNaN(date.getTime())) {
            return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
        }
    }
    return row.data_date;
};

const SeoGrowthTrendChart: React.FC<{ data: SeoWeeklySummaryPoint[] }> = ({ data }) => {
    const trafficRef = React.useRef<HTMLDivElement>(null);
    const rankRef = React.useRef<HTMLDivElement>(null);
    const trafficChart = React.useRef<echarts.ECharts | null>(null);
    const rankChart = React.useRef<echarts.ECharts | null>(null);

    React.useEffect(() => {
        if (!trafficRef.current || !rankRef.current) return;

        if (!trafficChart.current) {
            trafficChart.current = echarts.init(trafficRef.current);
        }
        if (!rankChart.current) {
            rankChart.current = echarts.init(rankRef.current);
        }

        const labels = data.map(formatWeekLabel);
        const impressions = data.map((row) => Number(row.impressions) || 0);
        const clicks = data.map((row) => Number(row.clicks) || 0);
        const ctr = data.map((row) => Number(row.ctr_percentage) || 0);
        const positions = data.map((row) => (
            row.average_position === null || row.average_position === undefined
                ? null
                : Number(row.average_position)
        ));

        const trafficOption: ECOption = {
            tooltip: {
                trigger: 'axis',
                valueFormatter: (value) => Number(value).toLocaleString(),
            },
            legend: { data: ['Impressions', 'Clicks'] },
            grid: { left: 56, right: 24, top: 36, bottom: 40 },
            xAxis: { type: 'category', data: labels, name: 'Week ending' },
            yAxis: { type: 'value', name: 'Count' },
            series: [
                {
                    name: 'Impressions',
                    type: 'bar',
                    data: impressions,
                    color: '#90caf9',
                    barMaxWidth: 36,
                },
                {
                    name: 'Clicks',
                    type: 'line',
                    data: clicks,
                    color: '#1565c0',
                    smooth: true,
                    symbolSize: 8,
                },
            ],
        };

        const rankOption: ECOption = {
            tooltip: {
                trigger: 'axis',
                formatter: (params) => {
                    const items = Array.isArray(params) ? params : [params];
                    const label = items[0]?.name || '';
                    const lines = items.map((item) => {
                        const value = Number(item.value);
                        if (item.seriesName === 'Avg position') {
                            return `${item.marker}${item.seriesName}: ${Number.isFinite(value) ? value.toFixed(2) : '—'} (lower is better)`;
                        }
                        return `${item.marker}${item.seriesName}: ${Number.isFinite(value) ? value.toFixed(2) : '—'}%`;
                    });
                    return [`Week ending ${label}`, ...lines].join('<br/>');
                },
            },
            legend: { data: ['Avg position', 'CTR'] },
            grid: { left: 56, right: 56, top: 36, bottom: 40 },
            xAxis: { type: 'category', data: labels, name: 'Week ending' },
            yAxis: [
                {
                    type: 'value',
                    name: 'Avg position',
                    inverse: true,
                    min: (value) => Math.max(1, Math.floor(value.min - 1)),
                    axisLabel: { formatter: (value: number) => value.toFixed(1) },
                },
                {
                    type: 'value',
                    name: 'CTR %',
                    position: 'right',
                    axisLabel: { formatter: (value: number) => `${value}%` },
                },
            ],
            series: [
                {
                    name: 'Avg position',
                    type: 'line',
                    yAxisIndex: 0,
                    data: positions,
                    color: '#6a1b9a',
                    smooth: true,
                    symbolSize: 8,
                    connectNulls: false,
                },
                {
                    name: 'CTR',
                    type: 'line',
                    yAxisIndex: 1,
                    data: ctr,
                    color: '#2e7d32',
                    smooth: true,
                    symbolSize: 8,
                },
            ],
        };

        trafficChart.current.setOption(trafficOption, true);
        rankChart.current.setOption(rankOption, true);

        const resizeHandler = () => {
            trafficChart.current?.resize();
            rankChart.current?.resize();
        };
        window.addEventListener('resize', resizeHandler);

        return () => {
            window.removeEventListener('resize', resizeHandler);
            trafficChart.current?.dispose();
            rankChart.current?.dispose();
            trafficChart.current = null;
            rankChart.current = null;
        };
    }, [data]);

    if (data.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Charts appear once at least one weekly snapshot is available.
            </Typography>
        );
    }

    return (
        <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 0.5 }}>Growth charts</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Weekly snapshots from Search Console. Position axis is inverted — line moving up means better rank.
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                    gap: 2,
                }}
            >
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ px: 1, pt: 0.5 }}>
                        Search traffic
                    </Typography>
                    <div ref={trafficRef} style={{ width: '100%', height: 320 }} />
                </Box>
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ px: 1, pt: 0.5 }}>
                        Rank &amp; click-through rate
                    </Typography>
                    <div ref={rankRef} style={{ width: '100%', height: 320 }} />
                </Box>
            </Box>
        </Box>
    );
};

export default SeoGrowthTrendChart;
